<template>
  <AnalyticsCard
    title="How people find you"
    subtitle="Grouped by channel, with how well each converts"
    :loading="loading"
    :empty="!loading && data.length === 0"
    empty-title="No traffic data yet"
    empty-hint="Once visitors arrive, this shows whether they came from search, a social post, or a link you sent directly."
    skeleton="list"
    :rows="5"
    :height="288"
  >
    <div class="divide-y divide-gray-50 dark:divide-gray-800/60">
      <div v-for="(source, idx) in data" :key="source.channel">
        <button type="button" @click="toggle(source.channel)"
          class="w-full relative flex items-center gap-3 px-5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-colors">
          <span class="absolute inset-y-1 left-1 rounded-md pointer-events-none transition-[width] duration-500"
            :style="{ width: `calc(${bar(source)}% - 0.5rem)`, backgroundColor: hexToRgba(color(idx), 0.12) }"></span>

          <span class="relative w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ backgroundColor: color(idx) }"></span>
          <span class="relative text-sm font-medium text-gray-800 dark:text-gray-100 truncate flex-1">{{ source.channel }}</span>

          <span class="relative text-[11px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
            :class="source.conversions > 0
              ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'">
            {{ source.conversions > 0 ? `${Math.round(source.conversion_rate * 100)}% booked` : 'none booked' }}
          </span>
          <span class="relative text-sm font-bold text-gray-900 dark:text-white tabular-nums w-10 text-right flex-shrink-0">
            {{ source.visitors }}
          </span>
        </button>

        <!-- The channel answers "is LinkedIn working". The domains behind it
             answer "which link", which is only ever the follow-up question. -->
        <div v-if="expanded.has(source.channel) && source.domains?.length"
          class="bg-gray-50/60 dark:bg-white/[0.02] divide-y divide-gray-100 dark:divide-gray-800/40">
          <div v-for="domain in source.domains" :key="domain.domain"
            class="flex items-center justify-between px-5 py-1.5 pl-11">
            <span class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ domain.domain }}</span>
            <span class="text-xs font-medium text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0">{{ domain.visitors }}</span>
          </div>
        </div>
      </div>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import { ref, computed } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { SERIES_COLORS, hexToRgba } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  data:    { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const expanded = ref(new Set())
const max = computed(() => Math.max(1, ...props.data.map(s => s.visitors)))

function toggle(channel) {
  const next = new Set(expanded.value)
  next.has(channel) ? next.delete(channel) : next.add(channel)
  expanded.value = next
}

function bar(source) {
  return Math.max(2, (source.visitors / max.value) * 100)
}

function color(idx) {
  return SERIES_COLORS[idx % SERIES_COLORS.length]
}
</script>
